import { gql, type Variables } from 'graphql-request';

import { getQueryClient } from '@/api/queryClient';
import logToDatadog from '@/logger/logToDatadog';
import gqlRequest from '@/utils/gqlRequest';

import {
  getCartIdCookie,
  invalidateCartData,
  saveCartIdCookie,
} from '../utils/cartUtils';
import type {
  AddToCart,
  AddToCart_cart_addLineItem,
  AddToCartVariables,
} from './types/AddToCart';

export type AddLineItemResult = AddToCart_cart_addLineItem;

export const query = gql`
  query AddToCart(
    $cartId: String
    $skuCode: String!
    $warrantySkuCode: String
  ) {
    cart(cartId: $cartId) {
      addLineItem(skuCode: $skuCode, warrantySkuCode: $warrantySkuCode) {
        cartId
        resultCategory
        errorCode
        invalidReason
      }
    }
  }
`;

export function withQueryKey(req: AddToCartVariables) {
  return {
    queryKey: ['addToCart', req.cartId, req.skuCode],
    queryFn: async () => {
      try {
        const response = await gqlRequest<AddToCart>(
          query,
          req as unknown as Variables,
        );
        const result = response.cart?.addLineItem;

        if (result?.resultCategory !== 'success')
          throw new Error('AddToCart failed');

        if (result.cartId) {
          saveCartIdCookie(result.cartId);
        }

        invalidateCartData();

        return result;
      } catch (error) {
        logToDatadog({
          error,
          message: 'AddToCart query failed',
          module: 'cart',
        });
        throw error;
      }
    },
  };
}

export async function addToCart(
  skuCode: string,
  warrantySkuCode: string | null = null,
): Promise<AddLineItemResult> {
  const cartId = getCartIdCookie();
  warrantySkuCode = warrantySkuCode ?? null;
  const queryClient = getQueryClient();

  return queryClient.fetchQuery({
    ...withQueryKey({ cartId, skuCode, warrantySkuCode }),
    staleTime: 0,
  });
}
